/**
 * WOW Effects
 * ─ Page load: announcement bar + header animate in
 * ─ Slide change: cinematic crossfade with zoom + blur out / scale in
 * ─ Slide content: staggered fade-up on each new slide
 * ─ Announcement bar: text swap fade-up
 */

/* ═══════════════════════════════════════════════════════════
   SLIDE IMAGE TRANSITION — cinematic overlay effect
   ═══════════════════════════════════════════════════════════ */

/**
 * For each slideshow-component on the page we intercept the
 * slideshow:select event and play a cinematic transition:
 *   1. Snapshot the current slide image as a fixed overlay
 *   2. Animate it: zoom-out + blur + fade-out
 *   3. Meanwhile the real slide scrolls instantly underneath
 *   4. New slide image scales up from 1.08 → 1 + fades in
 */
function initCinematicSlideTransition() {
  document.querySelectorAll('slideshow-component').forEach((slideshow) => {
    // Only apply to the hero/full-frame slideshow (not product card mini-slideshows)
    if (!slideshow.closest('.slideshow-section')) return;

    slideshow.addEventListener('slideshow:select', (e) => {
      const { slide: incomingSlide, previousIndex } = e.detail ?? {};
      if (!incomingSlide) return;

      const allSlides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
      const outgoingSlide = allSlides[previousIndex];

      if (outgoingSlide) animateOut(outgoingSlide);
      animateIn(incomingSlide);
    });

    // Animate the first slide in on load
    const firstSlide =
      slideshow.querySelector('slideshow-slide[aria-hidden="false"]') ||
      slideshow.querySelector('slideshow-slide');
    if (firstSlide) {
      setTimeout(() => animateIn(firstSlide), 300);
    }
  });
}

function animateOut(slide) {
  // Get the background image element inside the slide
  const img = slide.querySelector('img, video, .slideshow-slide__media, [class*="media"]');
  if (!img) return;

  img.classList.remove('wow-img-out');
  void img.offsetWidth;
  img.classList.add('wow-img-out');

  // Clean up after animation
  img.addEventListener('animationend', () => img.classList.remove('wow-img-out'), { once: true });
}

function animateIn(slide) {
  const img = slide.querySelector('img, video, .slideshow-slide__media, [class*="media"]');
  if (img) {
    img.classList.remove('wow-img-in');
    void img.offsetWidth;
    img.classList.add('wow-img-in');
    img.addEventListener('animationend', () => img.classList.remove('wow-img-in'), { once: true });
  }

  // Stagger the text content blocks
  animateSlideContent(slide);
}

/* ═══════════════════════════════════════════════════════════
   SLIDE CONTENT — staggered text/button animation
   ═══════════════════════════════════════════════════════════ */

function animateSlideContent(slide) {
  if (!slide) return;

  // Cast a wide net for content elements
  const targets = [
    ...slide.querySelectorAll(
      'h1, h2, h3, h4, h5, p, .button, a[class*="button"], [class*="btn"], ' +
      '[class*="text"], [class*="heading"], [class*="subheading"], [class*="eyebrow"]'
    ),
  ];

  // Fallback: direct children of the slide content wrapper
  if (!targets.length) {
    const wrapper = slide.querySelector('[class*="content"], [class*="slide__inner"]');
    if (wrapper) targets.push(...wrapper.children);
  }

  targets.forEach((el, i) => {
    el.classList.remove('wow-in');
    void el.offsetWidth;
    el.style.setProperty('--wow-i', String(i));
    el.classList.add('wow-in');
    el.addEventListener('animationend', () => el.classList.remove('wow-in'), { once: true });
  });
}

/* ═══════════════════════════════════════════════════════════
   ANNOUNCEMENT BAR — text swap animation
   ═══════════════════════════════════════════════════════════ */

function initAnnouncementEffects() {
  document.querySelectorAll('announcement-bar-component').forEach((bar) => {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'aria-hidden') continue;
        const slide = /** @type {HTMLElement} */ (m.target);
        if (slide.getAttribute('aria-hidden') === 'false') {
          slide.classList.remove('wow-announce');
          void slide.offsetWidth;
          slide.classList.add('wow-announce');
          slide.addEventListener('animationend', () => slide.classList.remove('wow-announce'), { once: true });
        }
      }
    });

    bar.querySelectorAll('.announcement-bar__slide').forEach((slide) => {
      observer.observe(slide, { attributes: true });
    });
  });
}

/* ═══════════════════════════════════════════════════════════
   PAGE LOAD — header + announcement bar entrance
   ═══════════════════════════════════════════════════════════ */

function initLoadAnimations() {
  const bar = document.querySelector('.announcement-bar');
  if (bar) bar.classList.add('wow-load-bar');

  const header = document.querySelector('#header-component') || document.querySelector('.header');
  if (header) header.classList.add('wow-load-header');
}

/* ═══════════════════════════════════════════════════════════
   BOOT
   ═══════════════════════════════════════════════════════════ */

function boot() {
  initLoadAnimations();
  initCinematicSlideTransition();
  initAnnouncementEffects();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
